import { Component, ErrorInfo, ReactNode } from "react";
import { Space, Stack, Text, Title } from "@mantine/core";
import { Button } from "./ui/Button";
import { Analytics } from "@/services/Analytics";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  fallback: ReactNode | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    fallback: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, fallback: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    // Track error in Mixpanel
    Analytics.error({
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      url: window.location.href,
    });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Stack
          align="center"
          justify="center"
          h="100vh"
          p="md"
          maw={600}
          mx="auto"
        >
          <Title order={1} c="primary.9">
            Something went wrong
          </Title>
          <Text c="dimmed" ta="center">
            We've been notified about this error and will look into it.
          </Text>
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload Page
          </Button>
          <Space h="xl" />

          <Text c="dimmed" ta="center">
            If the issue persists, you can contact our team directly by emailing
            dev@advizehub.com
          </Text>
        </Stack>
      );
    }

    return this.props.children;
  }
}
